// Main Vite entrypoint, loaded by vite_javascript_tag in the layouts.
// Application code lives in app/javascript; this file wires it all together.

import Rails from '@rails/ujs';
import '@rails/actiontext';
import * as ActiveStorage from '@rails/activestorage';
import axios from 'axios';
import { Tooltip, Popover, Dropdown, Modal, Collapse } from 'bootstrap';
import 'fontawesome-4.7/css/font-awesome.min.css';
import '../global/scss/index.scss';
import '@uppy/core/dist/style.min.css';
import '@uppy/dashboard/dist/style.min.css';
import '@uppy/image-editor/dist/style.min.css';
import ahoy from 'ahoy.js';
import 'trix';
import 'chartkick/chart.js';
import { Turbo } from '@hotwired/turbo-rails';
import ReactRailsUJS from 'react_ujs';
import '../controllers';

Rails.start();
ActiveStorage.start();

// Configure axios
const csrfToken = document.querySelector('meta[name=csrf-token]').content;
axios.defaults.headers.common['X-CSRF-Token'] = csrfToken;

// Setup React UJS for Turbo compatibility.
// Components are referenced by path-style names from views, e.g.
// react_component("Favorite/rails_binding") or react_component("pages/inbox/InboxPage").
const componentModules = import.meta.glob('../components/**/*.{js,jsx,ts,tsx}', { eager: true });
ReactRailsUJS.getConstructor = (name) => {
  for (const ext of ['tsx', 'ts', 'jsx', 'js']) {
    const mod =
      componentModules[`../components/${name}.${ext}`] || componentModules[`../components/${name}/index.${ext}`];
    if (mod) return mod.default || mod;
  }
  throw new Error(`React component not found: ${name}`);
};

// Manually handle React component mounting/unmounting for Turbo
document.addEventListener('turbo:load', function () {
  ReactRailsUJS.mountComponents();
});

document.addEventListener('turbo:before-render', function () {
  ReactRailsUJS.unmountComponents();
});

document.addEventListener('turbo:load', function () {
  // Initialize popovers
  document.querySelectorAll('[data-bs-toggle="popover"]').forEach((popover) => new Popover(popover));

  // Initialize dropdowns manually
  document.querySelectorAll('[data-bs-toggle="dropdown"]').forEach((dropdown) => new Dropdown(dropdown));

  // Initialize tooltips manually
  document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((tooltip) => new Tooltip(tooltip));

  // Initialize collapse toggles manually (e.g. the mobile post table of contents)
  document
    .querySelectorAll('[data-bs-toggle="collapse"]')
    .forEach((toggle) =>
      Collapse.getOrCreateInstance(document.querySelector(toggle.dataset.bsTarget), { toggle: false })
    );

  // Pop up modals automatically. Default: fire after a fixed delay.
  // If data-bs-auto-modal-trigger="engaged" is set, fire when the user has
  // shown engagement (scrolled into the content) OR after the delay as a
  // fallback. This avoids interrupting the first impression on pages that
  // already have plenty to browse, while still asking passive readers.
  document.querySelectorAll('[data-bs-auto-modal-delay]').forEach((modal) => {
    const delay = parseInt(modal.dataset.bsAutoModalDelay, 10) || 0;
    const trigger = modal.dataset.bsAutoModalTrigger;
    // How far past the viewport a user must scroll to count as "engaged".
    const ENGAGED_SCROLL_FRACTION = 0.6;
    let shown = false;
    const onScroll = () => {
      if (window.scrollY > window.innerHeight * ENGAGED_SCROLL_FRACTION) show();
    };
    const cleanup = () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('turbo:before-cache', cleanup);
    };
    const show = () => {
      // Bail if we navigated away before firing: the timer and scroll listener
      // live on window, so without this they'd log a phantom "displayed" event
      // (and try to open the modal) on an unrelated page.
      if (shown || !modal.isConnected) return;
      shown = true;
      cleanup();
      // Fire a "modal actually displayed" event (distinct from the server-side
      // "prompted" enrollment) so we can build a displayed -> submitted funnel.
      if (modal.dataset.bsAutoModalEvent && window.posthog) {
        let props = {};
        try {
          props = JSON.parse(modal.dataset.bsAutoModalEventProps || '{}');
        } catch (e) {
          props = {};
        }
        window.posthog.capture(modal.dataset.bsAutoModalEvent, props);
      }
      new Modal(modal).show();
    };
    if (trigger === 'engaged') {
      window.addEventListener('scroll', onScroll, { passive: true });
    }
    const timer = setTimeout(show, delay);
    document.addEventListener('turbo:before-cache', cleanup);
  });

  var parts = window.location.host.split('.');
  var subdomain = parts.splice(0, parts.length - 2).join('.');
  ahoy.trackView({ ...(window.railsParams || {}), subdomain });
});

/*
 * UJS to show/hide a element based on a checkbox or radio selection
 * Usage:
 *    <input type="checkbox" data-checkbox-show="#some-query-selector" />
 *    <div id="some-query-selector">...</div>
 * Now when the checkbox is checked, the element will be shown, otherwise hidden.
 * For the inverse behavior, use data-checkbox-hide instead of data-checkbox-show.
 * Also works on radio buttons.
 */
function bindCheckboxShowHide() {
  document.querySelectorAll('[data-checkbox-show]').forEach((checkbox) => {
    const selector = checkbox.dataset.checkboxShow;
    const targets = document.querySelectorAll(selector);
    if (!targets.length) return;
    const update = () => {
      if (checkbox.checked) {
        targets.forEach((target) => {
          target.querySelectorAll('input, textarea, select').forEach((el) => (el.disabled = false));
          target.classList.remove('d-none');
        });
      } else {
        targets.forEach((target) => {
          target.querySelectorAll('input, textarea, select').forEach((el) => (el.disabled = true));
          target.classList.add('d-none');
        });
      }
    };
    if (checkbox.type === 'radio') {
      // radio buttons needs listeners added to their sibling inputs as well,
      // as they can be de-selected when a sibling is chosen
      document.querySelectorAll(`[name="${checkbox.name}"]`).forEach((radio) => {
        radio.addEventListener('change', update);
      });
    } else {
      checkbox.addEventListener('change', update);
    }
    update();
  });

  document.querySelectorAll('[data-checkbox-hide]').forEach((checkbox) => {
    const selector = checkbox.dataset.checkboxHide;
    const targets = document.querySelectorAll(selector);
    if (!targets.length) return;
    const update = () => {
      if (checkbox.checked) {
        targets.forEach((target) => {
          target.querySelectorAll('input, textarea, select').forEach((el) => (el.disabled = true));
          target.classList.add('d-none');
        });
      } else {
        targets.forEach((target) => {
          target.querySelectorAll('input, textarea, select').forEach((el) => (el.disabled = false));
          target.classList.remove('d-none');
        });
      }
    };
    if (checkbox.type === 'radio') {
      // radio buttons needs listeners added to their sibling inputs as well,
      // as they can be de-selected when a sibling is chosen
      document.querySelectorAll(`[name="${checkbox.name}"]`).forEach((radio) => {
        radio.addEventListener('change', update);
      });
    } else {
      checkbox.addEventListener('change', update);
    }
    update();
  });
}
document.addEventListener('turbo:load', bindCheckboxShowHide);
document.addEventListener('turbo:render', bindCheckboxShowHide);

// home/index.html.haml
document.addEventListener('turbo:load', function () {
  if (document.getElementById('get-started')) {
    document.getElementById('get-started').addEventListener('click', function (e) {
      e.preventDefault();

      const targetElement = document.getElementById('select-a-school');
      const scrollTo = targetElement.offsetTop;

      // Smooth scroll to element
      window.scrollTo({
        top: scrollTo - 10,
        behavior: 'smooth',
      });
    });
  }
});

// listings/_form.html.haml
document.addEventListener('turbo:load', function () {
  if (document.getElementById('new-listing-form')) {
    const listingStartDate = document.getElementById('listing_start_date');
    const listingAvailable = document.getElementById('listing_available_now');
    const priceMaxArea = document.querySelector('.price-max-area');
    const listingPriceMax = document.getElementById('listing_price_max');
    const listingPriceRange = document.getElementById('listing_price_range');

    listingAvailable.addEventListener('click', function (e) {
      const isChecked = e.target.checked;
      const date = new Date();
      if (isChecked) {
        listingStartDate.valueAsDate = date;
      } else {
        listingStartDate.value = '';
      }
    });

    listingPriceRange.addEventListener('click', function () {
      priceMaxArea.classList.toggle('invisible');
      priceMaxArea.classList.toggle('visible');
      listingPriceMax.value = '';
    });

    const listingRelationship = document.getElementById('listing_relationship');
    listingRelationship.addEventListener('change', function (e) {
      console.log(e.target.value);
      const relationshipOther = document.querySelector('.relationship-other');
      if (e.target.value === 'Other') {
        relationshipOther.classList.remove('d-none');
        relationshipOther.classList.add('d-block');
      } else {
        relationshipOther.classList.remove('d-block');
        relationshipOther.classList.add('d-none');
      }
    });
  }
});

document.addEventListener('turbo:load', function () {
  // Detect when green cards come into view
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
      }
    });
  });

  document.querySelectorAll('.observe-visibility').forEach((el) => observer.observe(el));
});

// pricing/bulk.html.haml
document.addEventListener('turbo:load', function () {
  if (document.querySelector('.pricing.bulk')) {
    // Helper function to format price
    const formatPrice = (price) => {
      if (parseInt(price) === price) return price;
      return price.toFixed(2);
    };

    const update = () => {
      // Get input values
      const listingsInput = document.getElementById('listings');
      const schoolsInput = document.getElementById('schools');

      let numListings = parseInt(listingsInput.value);
      let numSchools = parseInt(schoolsInput.value);
      numListings = isNaN(numListings) ? 0 : numListings;
      numSchools = isNaN(numSchools) ? 0 : numSchools;

      // Get selected period
      const period = document.querySelector('[name="period"]:checked').value;

      // Calculate prices
      const monthlyPrice = 10 + (numSchools - 1) * 2.5;
      const yearlyPrice = 100 + (numSchools - 1) * 25;

      // Update price displays
      document.getElementById('monthly-price').textContent = `$${formatPrice(monthlyPrice)}`;
      document.getElementById('yearly-price').textContent = `$${formatPrice(yearlyPrice)}`;

      const price = period === 'month' ? monthlyPrice : yearlyPrice;
      const totalPrice = price * numListings;

      // Update UI based on validation
      const nextButtons = document.querySelectorAll('.next-btn');
      const totalElement = document.getElementById('total');
      const summaryElement = document.getElementById('summary');

      if (numListings < 5 || numSchools < 1) {
        nextButtons.forEach((btn) => (btn.disabled = true));
        totalElement.innerHTML = '';
        summaryElement.innerHTML = 'Please add at least 5 listings and 1 school.';
      } else {
        nextButtons.forEach((btn) => (btn.disabled = false));
        totalElement.innerHTML = `Total: $${formatPrice(totalPrice)} per ${period}`;
        summaryElement.innerHTML = `${numListings} listings at $${formatPrice(price)} per ${period} each`;
      }
    };

    // Add event listeners
    document.getElementById('listings').addEventListener('input', update);

    // Add listener to all period radio buttons
    document.querySelectorAll('[name="period"]').forEach((radio) => {
      radio.addEventListener('change', update);
    });

    document.getElementById('schools').addEventListener('change', update);

    // Initial update
    update();
  }
});

// Override Turbo's default browser confirm() with a Bootstrap modal.
// Triggered by data-turbo-confirm="..." on links/buttons/forms.
Turbo.setConfirmMethod((message) => {
  return new Promise((resolve) => {
    let modalEl = document.getElementById('turbo-confirm-modal');
    if (!modalEl) {
      modalEl = document.createElement('div');
      modalEl.id = 'turbo-confirm-modal';
      modalEl.className = 'modal fade';
      modalEl.tabIndex = -1;
      modalEl.setAttribute('aria-hidden', 'true');
      modalEl.innerHTML = `
        <div class="modal-dialog modal-sm modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-body" data-confirm-message></div>
            <div class="modal-footer py-2">
              <button type="button" class="btn btn-link btn-sm" data-bs-dismiss="modal" data-confirm-cancel>Cancel</button>
              <button type="button" class="btn btn-primary btn-sm" data-confirm-ok>Confirm</button>
            </div>
          </div>
        </div>
      `;
      document.body.appendChild(modalEl);
    }

    modalEl.querySelector('[data-confirm-message]').textContent = message;
    const modal = Modal.getOrCreateInstance(modalEl);
    const okBtn = modalEl.querySelector('[data-confirm-ok]');

    let result = false;
    const onOk = () => {
      result = true;
      modal.hide();
    };
    const onKeydown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onOk();
      }
    };
    const onShown = () => okBtn.focus();
    const onHidden = () => {
      okBtn.removeEventListener('click', onOk);
      modalEl.removeEventListener('keydown', onKeydown);
      modalEl.removeEventListener('shown.bs.modal', onShown);
      modalEl.removeEventListener('hidden.bs.modal', onHidden);
      resolve(result);
    };

    okBtn.addEventListener('click', onOk);
    modalEl.addEventListener('keydown', onKeydown);
    modalEl.addEventListener('shown.bs.modal', onShown);
    modalEl.addEventListener('hidden.bs.modal', onHidden);
    modal.show();
  });
});
